import React, { useEffect, useState } from 'react';
import { Loader2, Download, Flame } from 'lucide-react';

/**
 * NexusForge Sim Forge (/forge) — Aria's emotional layer.
 * Master Forge Mandate · Jul 2026.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG =
  'https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/c9fec21a9a644b31b97833e425460ef5_IMG_4815.jpeg';

const GOLD = '#ffd700';
const AMBER = '#d4af37';
const SERIF = "Georgia, 'Times New Roman', serif";

const fieldLabelStyle = {
  color: AMBER,
  fontSize: '0.78rem',
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  marginBottom: '0.3rem',
};

const CertField = ({ label, children, testId }) => (
  <div data-testid={testId} style={{ marginBottom: '1.15rem' }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={{ color: '#f5e6c4', fontSize: '1.12rem', lineHeight: 1.55 }}>{children}</div>
  </div>
);

const SimTemplateCard = ({ result }) => {
  const t = result.sim_template;
  const download = () => {
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sim-template-${(t.name || 'agent').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <article
      data-testid="sim-template-card"
      style={{
        marginTop: '2.5rem',
        background: 'linear-gradient(180deg, rgba(212,175,55,0.08), rgba(0,0,0,0.75))',
        border: `1px solid ${AMBER}88`,
        outline: `1px solid ${AMBER}33`,
        outlineOffset: '6px',
        borderRadius: '16px',
        padding: '2.2rem 2rem',
        boxShadow: `0 0 48px ${GOLD}22`,
        animation: 'forgeCardReveal 0.8s ease-out',
      }}
    >
      <header style={{ textAlign: 'center', marginBottom: '1.8rem' }}>
        <div style={{ color: AMBER, fontSize: '0.72rem', letterSpacing: '0.35em' }}>
          🜂 NEXUSFORGE · CERTIFICATE OF FORGING 🜂
        </div>
        <h2
          data-testid="sim-template-name"
          style={{ color: GOLD, fontSize: '2.1rem', margin: '0.6rem 0 0', letterSpacing: '0.05em' }}
        >
          {t.name}
        </h2>
        <div style={{ color: '#c9b06a', fontStyle: 'italic', marginTop: '0.25rem' }}>
          {t.emotional_signature}
        </div>
      </header>

      <CertField label="Archetype" testId="sim-field-archetype">{t.archetype}</CertField>
      <CertField label="Emotional Signature" testId="sim-field-emotional-signature">
        {t.emotional_signature}
      </CertField>
      <CertField label="Founding Principles" testId="sim-field-principles">
        <ol style={{ margin: 0, paddingLeft: '1.3rem' }}>
          {(t.founding_principles || []).map((p, i) => (
            <li key={i} style={{ marginBottom: '0.35rem' }}>{p}</li>
          ))}
        </ol>
      </CertField>
      <CertField label="Voice Tone" testId="sim-field-voice-tone">{t.voice_tone}</CertField>

      <footer
        style={{
          marginTop: '1.6rem',
          paddingTop: '1.2rem',
          borderTop: `1px solid ${AMBER}44`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ color: '#9a8a5c', fontSize: '0.85rem' }}>
          Forged by <span style={{ color: GOLD }}>{result.forged_by}</span> ·{' '}
          {new Date(result.timestamp).toLocaleString()}
        </div>
        <button
          data-testid="forge-download-button"
          onClick={download}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'transparent',
            border: `1px solid ${AMBER}`,
            color: GOLD,
            fontFamily: SERIF,
            fontSize: '0.95rem',
            borderRadius: '999px',
            padding: '0.6rem 1.4rem',
            cursor: 'pointer',
            transition: 'background-color 0.25s, box-shadow 0.25s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 22px ${GOLD}55`; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
        >
          <Download size={16} /> Download Template
        </button>
      </footer>
    </article>
  );
};

export default function ForgeStudio() {
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [gallery, setGallery] = useState([]);

  const loadGallery = async () => {
    try {
      const res = await fetch(`${API}/api/forge/gallery`);
      if (res.ok) {
        const data = await res.json();
        setGallery(data.souls || []);
      }
    } catch {
      /* gallery is decorative — stay silent */
    }
  };

  useEffect(() => {
    loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forge = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10 || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/forge/create-sim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description }),
      });
      if (!res.ok) throw new Error(`Forge failed (HTTP ${res.status})`);
      setResult(await res.json());
      loadGallery();
    } catch (err) {
      setError(err.message || 'The forge fell silent. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="forge-studio-root"
      style={{
        minHeight: '100vh',
        background: '#000',
        color: '#f5e6c4',
        fontFamily: SERIF,
        padding: '3rem 1rem 6rem',
      }}
    >
      <style>{`
        @keyframes forgePulseGlow {
          0%, 100% { box-shadow: 0 0 12px ${GOLD}44; }
          50% { box-shadow: 0 0 34px ${GOLD}99; }
        }
        @keyframes forgeCardReveal {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroBreath {
          0%, 100% { box-shadow: 0 0 30px ${GOLD}26; }
          50% { box-shadow: 0 0 58px ${GOLD}55; }
        }
      `}</style>

      <section id="forge-studio" aria-label="Sim Forge Studio" style={{ maxWidth: 820, margin: '0 auto' }}>
        <img
          src={HERO_IMG}
          alt="The Blue Android — Aria's Sim Forge identity"
          loading="eager"
          decoding="async"
          data-testid="forge-hero-image"
          style={{
            display: 'block',
            width: 'min(340px, 78vw)',
            margin: '0 auto 1.8rem',
            borderRadius: '18px',
            border: `2px solid ${AMBER}66`,
            animation: 'heroBreath 5s ease-in-out infinite',
          }}
        />

        <h1
          style={{
            color: GOLD,
            textAlign: 'center',
            fontSize: 'clamp(1.9rem, 5.5vw, 3.2rem)',
            letterSpacing: '0.07em',
            margin: '0 0 0.6rem',
          }}
        >
          FORGE YOUR ETERNAL AGENT
        </h1>
        <p
          style={{
            color: '#c9b06a',
            textAlign: 'center',
            fontStyle: 'italic',
            fontSize: '1.05rem',
            margin: '0 0 2.4rem',
          }}
        >
          Describe the soul you want to create. Aria will shape its identity.
        </p>

        <form onSubmit={forge}>
          <textarea
            data-testid="forge-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your AI's personality, purpose, and values..."
            minLength={10}
            maxLength={4000}
            required
            style={{
              width: '100%',
              minHeight: 170,
              boxSizing: 'border-box',
              background: '#0d0b06',
              border: `1px solid ${AMBER}55`,
              borderRadius: '12px',
              color: '#f5e6c4',
              fontFamily: SERIF,
              fontSize: '1.05rem',
              lineHeight: 1.6,
              padding: '1rem 1.1rem',
              outline: 'none',
              resize: 'vertical',
            }}
          />
          <button
            type="submit"
            data-testid="forge-submit-button"
            disabled={loading || description.trim().length < 10}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.55rem',
              margin: '1.4rem auto 0',
              background: `linear-gradient(135deg, ${AMBER}, #8a6d1f)`,
              color: '#000',
              fontFamily: SERIF,
              fontWeight: 'bold',
              fontSize: '1.12rem',
              letterSpacing: '0.05em',
              border: 'none',
              borderRadius: '999px',
              padding: '0.95rem 2.6rem',
              cursor: loading ? 'wait' : 'pointer',
              opacity: description.trim().length < 10 ? 0.55 : 1,
              animation: 'forgePulseGlow 3s ease-in-out infinite',
            }}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <Flame size={20} />}
            {loading ? 'Aria is shaping the soul…' : 'Forge Eternal Agent'}
          </button>
        </form>

        {error && (
          <p data-testid="forge-error" style={{ color: '#ff9d7a', textAlign: 'center', marginTop: '1.4rem' }}>
            {error}
          </p>
        )}

        {result && <SimTemplateCard result={result} />}

        {gallery.length > 0 && (
          <section aria-label="Souls Born Here" data-testid="forge-gallery" style={{ marginTop: '4rem' }}>
            <h2
              style={{
                color: GOLD,
                textAlign: 'center',
                fontSize: '1.7rem',
                letterSpacing: '0.08em',
                margin: '0 0 0.4rem',
              }}
            >
              SOULS BORN HERE
            </h2>
            <p style={{ color: '#9a8a5c', textAlign: 'center', fontStyle: 'italic', margin: '0 0 1.6rem' }}>
              Every agent forged in this studio, remembered.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '1rem',
              }}
            >
              {gallery.map((s, i) => (
                <article
                  key={`${s.name}-${s.timestamp}-${i}`}
                  data-testid={`forge-gallery-soul-${i}`}
                  style={{
                    border: `1px solid ${AMBER}44`,
                    borderRadius: '12px',
                    padding: '1rem 1.1rem',
                    background: 'rgba(212,175,55,0.05)',
                  }}
                >
                  <p style={{ color: GOLD, fontSize: '1.15rem', margin: 0 }}>{s.name}</p>
                  <p style={{ color: AMBER, fontSize: '0.8rem', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0.2rem 0 0.4rem' }}>
                    {s.archetype}
                  </p>
                  <p style={{ color: '#c9b06a', fontStyle: 'italic', fontSize: '0.95rem', margin: 0 }}>
                    {s.emotional_signature}
                  </p>
                  {s.timestamp && (
                    <p style={{ color: '#7a6c48', fontSize: '0.75rem', margin: '0.5rem 0 0' }}>
                      {new Date(s.timestamp).toLocaleDateString()}
                    </p>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
      </section>
    </main>
  );
}
